import { useState } from 'react';

import { Icon } from '../../../components/Icon';
import type { VectorSourceDescriptor } from '../../../data/model';
import { popupFields } from '../../../popups/fields';
import type { PopupFieldConfiguration } from '../../../popups/model';
import { popupConfigurationHasContent } from '../../../popups/validation';
import type { LayerDefinition } from '../../../project/model';
import type { PopupChangeResult } from '../../state/appStore';
import { useAppStore } from '../../state/useAppStore';
import { fieldDisplayLabel } from '../../../joins/effectiveLayer';

interface PopupsInspectorProps {
  readonly layer: LayerDefinition;
  readonly source: VectorSourceDescriptor;
}

interface PopupFieldRowProps {
  readonly configuration: PopupFieldConfiguration;
  readonly first: boolean;
  readonly last: boolean;
  readonly onLabelChange: (label: string) => void;
  readonly onMoveDown: () => void;
  readonly onMoveUp: () => void;
  readonly onVisibilityChange: (visible: boolean) => void;
  readonly sourceLabel: string;
  readonly configured: boolean;
}

function PopupFieldRow({
  configuration,
  first,
  last,
  onLabelChange,
  onMoveDown,
  onMoveUp,
  onVisibilityChange,
  sourceLabel,
  configured,
}: PopupFieldRowProps) {
  const [label, setLabel] = useState(configuration.label);

  const commitLabel = () => onLabelChange(label);

  return (
    <li className="popup-field-row">
      <input
        aria-label={`Show ${sourceLabel} in popup`}
        checked={configuration.visible}
        onChange={(event) => onVisibilityChange(event.target.checked)}
        type="checkbox"
      />
      <div className="popup-field-labels">
        <label htmlFor={`popup-label-${configuration.field}`}>Display label</label>
        <input
          aria-label={`Display label for ${sourceLabel}`}
          disabled={!configured}
          id={`popup-label-${configuration.field}`}
          onBlur={commitLabel}
          onChange={(event) => setLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
          value={label}
        />
        <span title={sourceLabel}>Source: {sourceLabel}</span>
      </div>
      <div aria-label={`Reorder ${sourceLabel}`} className="popup-field-actions">
        <button
          aria-label={`Move ${sourceLabel} up`}
          className="icon-button"
          disabled={first || !configured}
          onClick={onMoveUp}
          title="Move up"
          type="button"
        >
          <Icon name="arrow-up" />
        </button>
        <button
          aria-label={`Move ${sourceLabel} down`}
          className="icon-button"
          disabled={last || !configured}
          onClick={onMoveDown}
          title="Move down"
          type="button"
        >
          <Icon name="arrow-down" />
        </button>
      </div>
    </li>
  );
}

export function PopupsInspector({ layer, source }: PopupsInspectorProps) {
  const [notice, setNotice] = useState<string | null>(null);
  const setPopupsEnabled = useAppStore((state) => state.setLayerPopupsEnabled);
  const setTitleField = useAppStore((state) => state.setLayerPopupTitleField);
  const setFieldVisibility = useAppStore((state) => state.setLayerPopupFieldVisibility);
  const setFieldsVisible = useAppStore((state) => state.setLayerPopupFieldsVisible);
  const setFieldLabel = useAppStore((state) => state.setLayerPopupFieldLabel);
  const moveField = useAppStore((state) => state.moveLayerPopupField);
  const suitableFields = popupFields(source.fields);
  const fieldsByName = new Map(suitableFields.map((field) => [field.name, field]));
  const configuredFields = new Set(layer.popup.fields.map((field) => field.field));
  const displayedConfigurations = [
    ...layer.popup.fields,
    ...suitableFields
      .filter((field) => !configuredFields.has(field.name) && field.name !== layer.popup.titleField)
      .map((field) => ({ field: field.name, label: fieldDisplayLabel(field), visible: false })),
  ];
  const visibleFieldCount = layer.popup.fields.filter((field) => field.visible).length;

  const handleResult = (result: PopupChangeResult) => {
    setNotice(result.ok ? null : result.message);
  };

  return (
    <div
      className="inspector-content style-inspector popups-inspector"
      data-control-density="compact"
    >
      <section className="inspector-section labels-enable-section">
        <div className="labels-enable-row">
          <h3>Popups</h3>
          <button
            aria-checked={layer.popup.enabled}
            className="labels-toggle"
            disabled={!layer.popup.enabled && suitableFields.length === 0}
            onClick={() => handleResult(setPopupsEnabled(layer.id, !layer.popup.enabled))}
            role="switch"
            type="button"
          >
            {layer.popup.enabled ? 'On' : 'Off'}
          </button>
        </div>
        {!layer.popup.enabled ? (
          <p className="style-help">
            {suitableFields.length === 0
              ? 'No scalar attribute fields are available for popups.'
              : 'Turn on popups to let viewers inspect feature attributes.'}
          </p>
        ) : null}
        {notice ? (
          <p className="style-notice" role="status">
            {notice}
          </p>
        ) : null}
      </section>

      {layer.popup.enabled ? (
        <>
          <section className="inspector-section">
            <h3>Content</h3>
            <div className="style-field">
              <label htmlFor={`popup-title-field-${layer.id}`}>Title field</label>
              <select
                id={`popup-title-field-${layer.id}`}
                onChange={(event) =>
                  handleResult(setTitleField(layer.id, event.target.value || null))
                }
                value={layer.popup.titleField ?? ''}
              >
                <option value="">None</option>
                {suitableFields.map((field) => (
                  <option key={field.name} value={field.name}>
                    {fieldDisplayLabel(field)}
                  </option>
                ))}
              </select>
            </div>
            {!popupConfigurationHasContent(layer.popup) ? (
              <p className="style-notice" role="status">
                Choose at least one popup field or title.
              </p>
            ) : null}
          </section>

          <section className="inspector-section popup-fields-section">
            <div className="style-section-heading">
              <h3>Fields</h3>
              <span>
                {visibleFieldCount.toLocaleString()} of{' '}
                {displayedConfigurations.length.toLocaleString()} shown
              </span>
            </div>
            <div className="popup-field-bulk-actions" aria-label="Popup field visibility">
              <button
                disabled={visibleFieldCount === displayedConfigurations.length}
                onClick={() => handleResult(setFieldsVisible(layer.id, true))}
                type="button"
              >
                Select all
              </button>
              <button
                disabled={visibleFieldCount === 0}
                onClick={() => handleResult(setFieldsVisible(layer.id, false))}
                type="button"
              >
                Clear all
              </button>
            </div>
            <ul aria-label={`Popup fields for ${layer.name}`} className="popup-field-list">
              {displayedConfigurations.map((field, index) => (
                <PopupFieldRow
                  configured={configuredFields.has(field.field)}
                  configuration={field}
                  first={index === 0}
                  key={`${field.field}:${field.label}`}
                  last={index === displayedConfigurations.length - 1}
                  onLabelChange={(label) =>
                    handleResult(setFieldLabel(layer.id, field.field, label))
                  }
                  onMoveDown={() => handleResult(moveField(layer.id, field.field, 1))}
                  onMoveUp={() => handleResult(moveField(layer.id, field.field, -1))}
                  onVisibilityChange={(visible) =>
                    handleResult(setFieldVisibility(layer.id, field.field, visible))
                  }
                  sourceLabel={fieldDisplayLabel(
                    fieldsByName.get(field.field) ?? {
                      name: field.field,
                      type: 'unknown',
                      nullable: true,
                      presentCount: 0,
                      nullCount: 0,
                    },
                  )}
                />
              ))}
            </ul>
          </section>
        </>
      ) : null}
    </div>
  );
}
